import {
  defaultSubtitleEditorData,
  defaultSubtitleEditorLogoData,
} from '@/components/SubtitleEditor';
import { applyTemplateToEditor } from '@/lib/brand-kit/applyTemplateToEditor';
import { hydrateBrandKitWorkspace } from '@/lib/brand-kit/hydrateBrandKitWorkspace';
import { gateForSavedSettings, settleSelectedKit } from '@/lib/brand-kit/kitPrefillGate';
import {
  resolveInitialBrandKitId,
  type InitialBrandKitCandidate,
} from '@/lib/brand-kit/resolveInitialBrandKitId';
import { buildClipRenderInput } from '@/lib/export/buildClipRenderInput';
import { buildSubtitleLogoRenderConfig } from '@/lib/export/buildSubtitleLogoRenderConfig';
import { normalizeLayer } from '@/lib/layers/normalizeLayer';
import type { MediaLayer } from '@/lib/layers/types';
import { resolveClipTitleForBind } from '@/lib/layouts/layoutFieldBinds';
import { resolveLayoutChannelSource } from '@/lib/layouts/resolveLayoutChannelSource';
import { resolvePersistedProjectPreviewSettings } from '@/lib/preview/resolvePersistedProjectPreviewSettings';
import type { RenderFontCandidate } from '@/lib/resolveRenderFont';
import { resolveDefaultSubtitlesEnabled } from '@/lib/subtitles/resolveDefaultSubtitlesEnabled';
import { sanitizeWatermarkPosition } from '@/lib/watermark/watermarkPosition';

/** The project/library a clip belongs to, as `GET_MY_PROJECT` / `GET_MY_LIBRARY` load it. */
export interface StudioRenderSource {
  subtitle_config?: unknown;
  post_layout?: unknown;
  brand_kit?: { id?: string | null } | null;
  speaker_layout?: unknown;
  watermark_position?: unknown;
  default_subtitles_enabled?: boolean | null;
  youtube_channels?: Array<{ name?: string | null; thumbnail?: string | null }> | null;
}

export interface StudioRenderKit extends InitialBrandKitCandidate {
  data?: unknown;
}

/**
 * The `renderLibraryClip` / `renderProjectClipV2` input an export of this clip would send from
 * the editor, rebuilt from what the editor saved: FreeCut Studio asks the backend for that
 * export's render data, so a clip opens in the studio exactly as it would export.
 *
 * Same rules as the editor: the saved subtitle settings and layout win over the template, a
 * never-styled clip takes its template's (the saved kit, else the fallback kit), and the layers
 * saved on the clip are sent. The orientation is vertical.
 */
export function buildStudioRenderInput({
  source,
  clip,
  brandKits,
  hasProFeatures,
  fonts,
  user,
  fillerPool,
  random,
}: {
  source: StudioRenderSource;
  clip: { name?: string | null; duration_seconds?: number | null; data?: unknown };
  brandKits: readonly StudioRenderKit[];
  hasProFeatures: boolean;
  fonts: readonly RenderFontCandidate[] | null | undefined;
  user: { name?: string | null; profile_picture?: string | null } | null | undefined;
  fillerPool: string[];
  random?: () => number;
}): Record<string, unknown> {
  const savedBrandKitId = source.brand_kit?.id ?? null;
  const brandKitId = resolveInitialBrandKitId({ savedBrandKitId, kits: brandKits, hasProFeatures });
  const saved = resolvePersistedProjectPreviewSettings({
    project: source,
    brandKits: [...brandKits],
    hasProFeatures,
    defaultSubtitleData: defaultSubtitleEditorData,
    defaultLogoData: defaultSubtitleEditorLogoData,
    // The backend applies campaign enforcement to every export itself.
    effectivePostLayout: null,
  });
  let look = {
    subtitleData: saved.subtitleData,
    logoData: saved.logoData,
    fontId: saved.fontId,
    postLayout: saved.postLayout,
    fieldBinds: saved.fieldBinds,
    speakerLayout: saved.speakerLayout,
  };

  const kit = brandKitId ? brandKits.find((candidate) => candidate.id === brandKitId) : undefined;
  const { prefill } = kit
    ? settleSelectedKit(
      gateForSavedSettings({ hasSavedSettings: !!source.subtitle_config, savedKitId: savedBrandKitId }),
      kit.id,
    )
    : { prefill: false };
  if (prefill && kit?.data) {
    const workspace = hydrateBrandKitWorkspace(kit.data);
    const applied = applyTemplateToEditor(saved.postLayout, kit.data);
    look = {
      subtitleData: workspace.subtitle,
      logoData: workspace.logo,
      fontId: workspace.fontId || saved.fontId,
      postLayout: applied.postLayout,
      fieldBinds: applied.fieldBinds,
      speakerLayout: 1,
    };
  }

  const subtitlesEnabled = resolveDefaultSubtitlesEnabled(source);
  const channel = resolveLayoutChannelSource(source);
  const clipDurationSec = clip.duration_seconds ?? 0;
  const editorLayers = (clip.data as { editorLayers?: MediaLayer[] } | null | undefined)?.editorLayers;

  return buildClipRenderInput({
    format: 'vertical',
    subtitlesEnabled,
    renderExtras: buildSubtitleLogoRenderConfig({
      subtitlesEnabled,
      subtitleData: look.subtitleData,
      logoData: look.logoData,
      selectedSubtitleFontId: look.fontId,
      fonts,
    }),
    brandKitId,
    layout: {
      postLayout: look.postLayout,
      fieldBinds: look.fieldBinds,
      clipTitle: resolveClipTitleForBind(clip.name),
      channelName: channel?.name,
      channelAvatarUrl: channel?.thumbnail,
      userName: user?.name ?? undefined,
      userAvatarUrl: user?.profile_picture ?? undefined,
      speakerLayout: look.speakerLayout,
      // The speaker picker only offers a split layout for 2+ detected speakers, so a saved one
      // stands without downloading the scene detection again.
      uniqueSpeakerCount: look.speakerLayout,
      fillerPool,
      clipDurationSec,
      random,
    },
    // Without saved layers the backend seeds the detected facecam, as on export.
    layers: (editorLayers ?? []).map(normalizeLayer),
    layerClipDurationSec: clipDurationSec,
    watermarkPosition: sanitizeWatermarkPosition(source.watermark_position),
  });
}
